/**
 * Zustand 持久化中间件
 * 提供统一的本地存储持久化功能
 */

import type { StateCreator } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * 持久化配置选项
 */
export interface PersistOptions<T> {
  /** 存储的 key 名称 */
  name: string;
  /** 需要持久化的字段（可选，默认全部持久化） */
  partialize?: (state: T) => Partial<T>;
  /** 存储引擎（默认 localStorage） */
  storage?: Storage;
  /**
   * 持久化 schema 版本；与 `migrate` 配合，在本地缓存结构与当前代码不一致时做一次迁移。
   * @see https://docs.pmnd.rs/zustand/integrations/persisting-store-data#versioning
   */
  version?: number;
  /** 从旧版本缓存恢复时合并 / 清洗字段 */
  migrate?: (persistedState: unknown, version: number) => unknown | Promise<unknown>;
}

/**
 * 创建带持久化的 Store
 * 
 * @param config - Store 配置
 * @param options - 持久化选项
 * @returns 带持久化的 Store
 * 
 * @example
 * ```ts
 * export const useAuthStore = createPersistedStore(
 *   (set) => ({
 *     token: null,
 *     setToken: (token) => set({ token }),
 *   }),
 *   { name: 'auth-storage' }
 * );
 * ```
 */
export function createPersistedStore<T>(
  config: StateCreator<T, [], []>,
  options: PersistOptions<T>
) {
  return persist<T>(config, {
    name: options.name,
    storage: createJSONStorage(() => options.storage || localStorage),
    partialize: options.partialize,
    ...(options.version !== undefined ? { version: options.version } : {}),
    ...(options.migrate !== undefined ? { migrate: options.migrate } : {}),
  });
}

/**
 * 清除指定 store 的持久化数据
 * 
 * @param storeName - Store 名称
 */
export function clearPersistedStore(storeName: string): void {
  localStorage.removeItem(`storage-${storeName}`);
}

/**
 * 清除所有持久化数据
 */
export function clearAllPersistedStores(): void {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('storage-')) {
      localStorage.removeItem(key);
    }
  });
}
